import React from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Paper,
  Button,
  Chip,
  Stack,
  useTheme,
} from "@mui/material";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import CollectionsOutlinedIcon from "@mui/icons-material/CollectionsOutlined";
import StarOutlineRoundedIcon from "@mui/icons-material/StarOutlineRounded";
import WbSunnyOutlinedIcon from "@mui/icons-material/WbSunnyOutlined";
import CampaignOutlinedIcon from "@mui/icons-material/CampaignOutlined";

function HomePageMangement() {
  const navigate = useNavigate();
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  const colors = {
    bg: isDark ? "#11100F" : "#F6F3EF",
    card: isDark ? "#191715" : "#FFFFFF",
    text: isDark ? "#F4F0EA" : "#27231F",
    secondary: isDark ? "#AAA49C" : "#77716A",
    accent: isDark ? "#D1B99A" : "#806B52",
    accentSoft: isDark ? "rgba(209,185,154,.12)" : "rgba(128,107,82,.08)",
    border: isDark ? "rgba(255,255,255,.08)" : "rgba(50,40,30,.09)",
  };

  const features = [
    {
      name: "Banners",
      key: "banner",
      description: "Manage homepage banners and promotional visuals",
      icon: <CollectionsOutlinedIcon />,
    },
    {
      name: "Top Products",
      key: "top-product",
      description: "Curate products highlighted across the storefront",
      icon: <StarOutlineRoundedIcon />,
    },
    // {
    //   name: "Seasonal Collection",
    //   key: "seasonal-collection",
    //   description: "Manage seasonal and occasion-based collections",
    //   icon: <WbSunnyOutlinedIcon />,
    // },
    // {
    //   name: "Sponsored Products",
    //   key: "sponsered-products",
    //   description: "Control products promoted across the homepage",
    //   icon: <CampaignOutlinedIcon />,
    // },
  ];

  return (
    <Box
      sx={{
        minHeight: "100%",
        background: colors.bg,
        px: { xs: 2, sm: 3, md: 4 },
        py: { xs: 3, md: 4 },
      }}
    >
      <Box
        sx={{
          maxWidth: 1050,
          mx: "auto",
        }}
      >
        <Box sx={{ mb: 4 }}>
          <Typography
            sx={{
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: ".13em",
              textTransform: "uppercase",
              color: colors.accent,
              mb: 1,
            }}
          >
            Storefront Management
          </Typography>

          <Typography
            sx={{
              fontFamily: "Georgia, serif",
              fontSize: { xs: 28, sm: 34 },
              fontWeight: 500,
              lineHeight: 1.15,
              color: colors.text,
            }}
          >
            Manage Home Page
          </Typography>

          <Typography
            sx={{
              mt: 1,
              maxWidth: 650,
              fontSize: 13,
              lineHeight: 1.7,
              color: colors.secondary,
            }}
          >
            Curate the content customers see first when they visit the Hindustan
            Wardrobe storefront.
          </Typography>
        </Box>

        <Paper
          elevation={0}
          sx={{
            background: colors.card,
            border: `1px solid ${colors.border}`,
            borderRadius: "16px",
            overflow: "hidden",
            boxShadow: "0 10px 35px rgba(40,30,20,.045)",
          }}
        >
          <Box
            sx={{
              px: { xs: 2, sm: 3 },
              py: 2,
              borderBottom: `1px solid ${colors.border}`,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 2,
            }}
          >
            <Box>
              <Typography
                sx={{
                  fontFamily: "Georgia, serif",
                  fontSize: 19,
                  color: colors.text,
                  fontWeight: 600,
                }}
              >
                Homepage Sections
              </Typography>

              <Typography
                sx={{
                  mt: 0.35,
                  fontSize: 11,
                  color: colors.secondary,
                }}
              >
                Select a section to manage its storefront content
              </Typography>
            </Box>

            <Chip
              label={`${features.length} Sections`}
              size="small"
              sx={{
                height: 28,
                borderRadius: "8px",
                background: colors.accentSoft,
                color: colors.accent,
                fontSize: 11,
                fontWeight: 700,
              }}
            />
          </Box>

          <Stack
            divider={
              <Box sx={{ borderBottom: `1px solid ${colors.border}` }} />
            }
          >
            {features.map((feature, index) => (
              <Box
                key={feature.key}
                sx={{
                  px: { xs: 2, sm: 3 },
                  py: 2.2,
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  transition: "background .2s ease",
                  "&:hover": {
                    background: colors.accentSoft,
                  },
                }}
              >
                <Box
                  sx={{
                    width: 46,
                    height: 46,
                    flexShrink: 0,
                    borderRadius: "12px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    background: colors.accentSoft,
                    color: colors.accent,
                  }}
                >
                  {React.cloneElement(feature.icon, {
                    sx: { fontSize: 21 },
                  })}
                </Box>

                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      flexWrap: "wrap",
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: 14,
                        fontWeight: 700,
                        color: colors.text,
                      }}
                    >
                      {feature.name}
                    </Typography>

                    <Chip
                      label={`0${index + 1}`}
                      size="small"
                      sx={{
                        height: 21,
                        minWidth: 30,
                        borderRadius: "6px",
                        background: "transparent",
                        border: `1px solid ${colors.border}`,
                        color: colors.secondary,
                        fontSize: 9,
                        fontWeight: 700,
                      }}
                    />
                  </Box>

                  <Typography
                    sx={{
                      mt: 0.5,
                      fontSize: 12,
                      color: colors.secondary,
                      lineHeight: 1.5,
                    }}
                  >
                    {feature.description}
                  </Typography>
                </Box>

                <Button
                  variant="outlined"
                  onClick={() => navigate(feature.key)}
                  endIcon={<ArrowForwardRoundedIcon sx={{ fontSize: 16 }} />}
                  sx={{
                    flexShrink: 0,
                    minWidth: { xs: 40, sm: 112 },
                    height: 38,
                    px: { xs: 1, sm: 1.7 },
                    borderRadius: "9px",
                    borderColor: colors.border,
                    color: colors.text,
                    textTransform: "none",
                    fontSize: 12,
                    fontWeight: 700,
                    "&:hover": {
                      borderColor: colors.accent,
                      background: colors.accentSoft,
                      color: colors.accent,
                    },
                    "& .MuiButton-endIcon": {
                      ml: { xs: 0, sm: 0.5 },
                    },
                  }}
                >
                  <Box
                    component="span"
                    sx={{
                      display: { xs: "none", sm: "inline" },
                    }}
                  >
                    Manage
                  </Box>
                </Button>
              </Box>
            ))}
          </Stack>
        </Paper>

        <Box
          sx={{
            mt: 2,
            px: 1,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography
            sx={{
              fontSize: 11,
              color: colors.secondary,
            }}
          >
            Changes made here are reflected on the customer storefront.
          </Typography>

          <Typography
            sx={{
              fontSize: 11,
              color: colors.accent,
              fontWeight: 600,
            }}
          >
            Hindustan Wardrobe
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}

export default HomePageMangement;
